import Fuse, { type FuseResult, type IFuseOptions } from "fuse.js";
import { getContentFallbackChain, isRegionalLocale, isValidLocale, locales } from "@/config/locale";
import type { FuseSearchResult, SearchIndexProduct, SearchMatchStrategy, SearchProduct } from "./types";

const MIN_QUERY_LENGTH = 2;
const STRICT_THRESHOLD = 0.4;
const LOOSE_THRESHOLD = 0.6;
const DID_YOU_MEAN_SCORE_CUTOFF = 0.5;
const looseFuseCache = new WeakMap<SearchIndexProduct[], Fuse<SearchIndexProduct>>();
/**
 * Every shop locale except `en`, which is the base `name` field and has no `translations.en` key,
 * and the regional locales (es-mx), which have no field in the index. Fuse normalizes key weights,
 * so an empty extra key would shift every score.
 */
export const TRANSLATION_LOCALES = locales.filter((locale) => locale !== "en" && !isRegionalLocale(locale));

/**
 * Index field a locale reads: the first locale of its content chain that has one, so the regional
 * es-mx reads the Spanish names (`es`, from `translationEs`) of the shared per-channel index. Every
 * other locale reads its own field, as before.
 */
function getIndexLocale(locale: string): string {
	if (!isValidLocale(locale) || !isRegionalLocale(locale)) return locale;
	return (
		getContentFallbackChain(locale).find(
			(candidate) => candidate === "en" || (TRANSLATION_LOCALES as readonly string[]).includes(candidate),
		) ?? locale
	);
}

const MATCH_PRIORITY: Record<SearchMatchStrategy, number> = {
	exact: 0,
	alias: 1,
	translation: 2,
	normalized: 3,
	fuzzy: 4,
	recovery: 5,
};

/** Fuse 7+ supports `useTokenSearch`; some `IFuseOptions` typings omit it. */
type FuseProductOptions = IFuseOptions<SearchIndexProduct> & { useTokenSearch?: boolean };

const FUSE_OPTIONS: FuseProductOptions = {
	keys: [
		{ name: "searchTerms", weight: 1 },
		{ name: "compactSearchTerms", weight: 0.98 },
		{ name: "aliases", weight: 0.95 },
		{ name: "name", weight: 0.9 },
		...TRANSLATION_LOCALES.map((locale) => ({
			name: `translations.${locale}`,
			weight: 0.85,
		})),
		{ name: "slug", weight: 0.8 },
		{ name: "categoryName", weight: 0.3 },
		{
			name: "categoryTranslations",
			getFn: (product) => Object.values(product.categoryTranslations ?? {}),
			weight: 0.3,
		},
	],
	threshold: STRICT_THRESHOLD,
	includeScore: true,
	includeMatches: true,
	ignoreDiacritics: true,
	useTokenSearch: true,
	minMatchCharLength: MIN_QUERY_LENGTH,
};

/**
 * Canonical comparison form shared by aliases, index terms and submitted queries.
 * Hyphens, slashes and punctuation intentionally become token separators so
 * "PT-141", "PT 141" and "PT/141" converge on the same value.
 */
export function normalizeSearchQuery(value: string): string {
	return value
		.normalize("NFKD")
		.replace(/\p{M}+/gu, "")
		.toLocaleLowerCase("en")
		.replace(/[\p{P}\p{S}_]+/gu, " ")
		.replace(/\s+/g, " ")
		.trim();
}

export function compactSearchQuery(value: string): string {
	return normalizeSearchQuery(value).replaceAll(" ", "");
}

/** Create a reusable Fuse instance with normalized and compact index terms. */
export function createFuseInstance(products: SearchIndexProduct[]): Fuse<SearchIndexProduct> {
	return new Fuse(products.map(prepareProduct), FUSE_OPTIONS);
}

/** Search products using Fuse.js and return deterministic exact-before-fuzzy results. */
export function fuseSearch(
	fuse: Fuse<SearchIndexProduct>,
	query: string,
	limit?: number,
	locale = "en",
): SearchProduct[] {
	const normalized = getSearchableQuery(query);
	if (!normalized) return [];

	return rankResults(fuse.search(normalized), query, locale)
		.slice(0, limit)
		.map(({ item, score, matches, matchStrategy }) =>
			toSearchProduct(item, locale, matchStrategy, score, matches),
		);
}

/**
 * Search with a deliberately looser second pass used only for a correction
 * suggestion. Loose matches are never presented as normal product results.
 */
export function fuseSearchWithDidYouMean(
	fuse: Fuse<SearchIndexProduct>,
	query: string,
	locale: string,
	limit?: number,
	products?: SearchIndexProduct[],
): FuseSearchResult {
	const normalized = getSearchableQuery(query);
	if (!normalized) return { products: [] };

	const strictResults = rankResults(fuse.search(normalized), query, locale);
	if (strictResults.length > 0) {
		return {
			products: strictResults
				.slice(0, limit)
				.map(({ item, score, matches, matchStrategy }) =>
					toSearchProduct(item, locale, matchStrategy, score, matches),
				),
		};
	}

	if (products && products.length > 0) {
		const looseResults = getLooseFuseInstance(products).search(normalized, { limit: 1 });
		if (looseResults.length > 0 && (looseResults[0].score ?? 1) < DID_YOU_MEAN_SCORE_CUTOFF) {
			return {
				products: [],
				didYouMean: getLocalizedName(looseResults[0].item, locale),
			};
		}
	}

	return { products: [] };
}

function getLocalizedName(product: SearchIndexProduct, locale: string): string {
	const indexLocale = getIndexLocale(locale);
	if (indexLocale === "en") return product.name;
	return product.translations[indexLocale] || product.name;
}

export function toSearchProduct(
	item: SearchIndexProduct,
	locale: string,
	matchStrategy: SearchMatchStrategy = "fuzzy",
	score?: number,
	matches?: ReadonlyArray<{ key?: string; indices: ReadonlyArray<readonly [number, number]> }>,
): SearchProduct {
	const indexLocale = getIndexLocale(locale);
	const localizedName = getLocalizedName(item, locale);
	const matchedNameKey = indexLocale === "en" ? "name" : `translations.${indexLocale}`;
	const nameHighlight = getHighlightMarkup(
		localizedName,
		matches?.find((match) => match.key === matchedNameKey)?.indices,
	);

	return {
		id: item.id,
		name: localizedName,
		slug: item.slug,
		thumbnailUrl: item.thumbnailUrl,
		thumbnailAlt: item.thumbnailAlt,
		price: item.price,
		currency: item.currency,
		categoryName: item.categoryTranslations?.[indexLocale] || item.categoryName,
		isAvailable: item.isAvailable,
		matchStrategy,
		highlights: nameHighlight ? { name: nameHighlight } : undefined,
		score: score != null ? 1 - score : undefined,
	};
}

function prepareProduct(product: SearchIndexProduct): SearchIndexProduct {
	const rawTerms = [product.name, product.slug, ...Object.values(product.translations), ...product.aliases];
	const searchTerms = unique(rawTerms.map(normalizeSearchQuery).filter(Boolean));
	const compactSearchTerms = unique(rawTerms.map(compactSearchQuery).filter(Boolean));

	return {
		...product,
		searchTerms,
		compactSearchTerms,
	};
}

function rankResults(
	results: FuseResult<SearchIndexProduct>[],
	query: string,
	locale: string,
): Array<FuseResult<SearchIndexProduct> & { matchStrategy: SearchMatchStrategy }> {
	return results
		.map((result) => ({
			...result,
			matchStrategy: classifyMatch(result.item, query, locale),
		}))
		.sort((left, right) => {
			const priority = MATCH_PRIORITY[left.matchStrategy] - MATCH_PRIORITY[right.matchStrategy];
			if (priority !== 0) return priority;
			return (left.score ?? 1) - (right.score ?? 1);
		});
}

function classifyMatch(product: SearchIndexProduct, query: string, locale: string): SearchMatchStrategy {
	const normalized = normalizeSearchQuery(query);
	const compact = compactSearchQuery(query);
	const baseTerms = [product.name, product.slug];
	if (baseTerms.some((term) => normalizeSearchQuery(term) === normalized)) return "exact";

	if (
		product.aliases.some(
			(alias) => normalizeSearchQuery(alias) === normalized || compactSearchQuery(alias) === compact,
		)
	) {
		return "alias";
	}

	const localizedName = product.translations[getIndexLocale(locale)];
	if (
		localizedName &&
		(normalizeSearchQuery(localizedName) === normalized || compactSearchQuery(localizedName) === compact)
	) {
		return "translation";
	}

	const allNames = [product.name, product.slug, ...Object.values(product.translations)];
	if (allNames.some((term) => compactSearchQuery(term) === compact)) return "normalized";
	return "fuzzy";
}

function getSearchableQuery(query: string): string | null {
	const normalized = normalizeSearchQuery(query);
	return normalized.length >= MIN_QUERY_LENGTH ? normalized : null;
}

function getLooseFuseInstance(products: SearchIndexProduct[]): Fuse<SearchIndexProduct> {
	const cached = looseFuseCache.get(products);
	if (cached) return cached;

	const looseFuse = new Fuse(products.map(prepareProduct), {
		...FUSE_OPTIONS,
		threshold: LOOSE_THRESHOLD,
	});
	looseFuseCache.set(products, looseFuse);
	return looseFuse;
}

function unique(values: string[]): string[] {
	return [...new Set(values)];
}

function getHighlightMarkup(
	value: string,
	indices?: ReadonlyArray<readonly [number, number]>,
): string | undefined {
	if (!indices || indices.length === 0) return undefined;

	let cursor = 0;
	let highlighted = "";
	for (const [start, end] of indices) {
		if (start > cursor) highlighted += escapeHtml(value.slice(cursor, start));
		highlighted += `<mark>${escapeHtml(value.slice(start, end + 1))}</mark>`;
		cursor = end + 1;
	}

	if (cursor < value.length) highlighted += escapeHtml(value.slice(cursor));
	return highlighted;
}

function escapeHtml(value: string): string {
	return value
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;")
		.replaceAll("'", "&#39;");
}
